import { warn } from '@execonline-inc/logging';
import { fromNullable } from 'maybeasy';
import { useEffect, useState } from 'react';
import { Task } from 'taskarian';
import { findLinkByT } from '../../../../../../../../../Links';
import * as Resourceable from '../../../../../../../../../Resourceable';
import {
  ExperienceDetailResource,
  ExperienceResource,
} from '../../../../../../CurrentCollectionStep/BuilderV2/Types';
import { errored, loading, ready, State } from './Types';

const experienceCache = new Map<string, ExperienceDetailResource>();
const fetchFromServer = (
  experienceResource: ExperienceResource,
): Task<string, ExperienceDetailResource> =>
  Task.succeed(experienceResource.links)
    .andThen(findLinkByT({ rel: 'details' }))
    .andThen(Resourceable.fetch)
    .map(({ resource }) => resource)
    .mapError(() => 'unable to fetch resource from server');

export function useFetchExperienceDetail(experienceResource: ExperienceResource) {
  const cacheKey = String(experienceResource.payload.id);
  const [state, setState] = useState<State>(() =>
    fromNullable(experienceCache.get(cacheKey)).map<State>(ready).getOrElse(loading),
  );

  useEffect(() => {
    // If we already have data from the cache, nothing to fetch
    if (state.kind === 'ready' || state.kind === 'errored') {
      return;
    }

    // Start loading and fetch
    setState(loading());
    const cancel = fetchFromServer(experienceResource).fork(
      (e: string) => {
        warn(e);
        setState(errored(e));
      },
      (resource: ExperienceDetailResource) => {
        experienceCache.set(cacheKey, resource);
        setState(ready(resource));
      },
    );

    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return state;
}
